import { useMemo, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { Shield, Loader2, AlertCircle, Check, KeyRound } from 'lucide-react'
import { resetPassword } from '../api/client'

export default function ResetPassword() {
  const [searchParams] = useSearchParams()
  const token = useMemo(() => searchParams.get('token') || '', [searchParams])

  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [done, setDone] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    if (!token) { setError('Reset link is missing a token.'); return }
    if (password.length < 8) { setError('Password must be at least 8 characters.'); return }
    if (!/[A-Za-z]/.test(password) || !/\d/.test(password)) {
      setError('Password must contain at least one letter and one number.'); return
    }
    if (password !== confirmPassword) { setError('Passwords do not match.'); return }

    setSubmitting(true)
    try {
      await resetPassword(token, password)
      setDone(true)
    } catch (err) {
      setError((err as { message?: string })?.message || 'Reset failed. The link may have expired.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="relative min-h-[calc(100vh-3.5rem)] flex items-center justify-center px-4 py-12">
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute left-1/2 -top-24 -translate-x-1/2 h-96 w-[40rem] bg-brand-500/10 blur-3xl rounded-full" />
      </div>

      <div className="relative w-full max-w-md">
        <div className="card p-8 md:p-10 space-y-6">
          <div className="text-center space-y-3">
            <div className="mx-auto w-14 h-14 rounded-2xl bg-gradient-to-br from-brand-500 to-brand-700 ring-4 ring-brand-500/10 flex items-center justify-center shadow-lg">
              <Shield size={28} className="text-white" strokeWidth={2.2} />
            </div>
            <div>
              <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-gray-900 dark:text-gray-50">
                Reset your password
              </h1>
              <p className="mt-1.5 text-sm text-gray-500 dark:text-gray-400">
                Enter a new password for your IDFY account.
              </p>
            </div>
          </div>

          {!token && (
            <div className="flex items-start gap-2 p-3 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 text-sm">
              <AlertCircle size={16} className="flex-shrink-0 mt-0.5" />
              <span>This reset link is missing a token. Request a new link from the sign-in page.</span>
            </div>
          )}

          {done ? (
            <div className="space-y-4">
              <div className="flex items-start gap-2 p-4 rounded-lg bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400 text-sm">
                <Check size={16} className="flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium">Password updated.</p>
                  <p className="mt-0.5 text-[13px] text-emerald-700/80 dark:text-emerald-400/80">
                    You can now sign in with your new password.
                  </p>
                </div>
              </div>
              <Link to="/signin" className="btn-primary w-full justify-center">Continue to sign in</Link>
            </div>
          ) : token && (
            <>
              {error && (
                <div className="flex items-start gap-2 p-3 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 text-sm">
                  <AlertCircle size={16} className="flex-shrink-0 mt-0.5" />
                  <span>{error}</span>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label htmlFor="password" className="label flex items-center gap-1.5">
                    <KeyRound size={13} /> New password
                  </label>
                  <input id="password" type="password" autoComplete="new-password" value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    minLength={8} maxLength={64} required className="input" />
                  <p className="mt-1 text-[11px] text-gray-500 dark:text-gray-400">At least 8 characters, with letters and numbers.</p>
                </div>

                <div>
                  <label htmlFor="confirmPassword" className="label">Confirm new password</label>
                  <input id="confirmPassword" type="password" autoComplete="new-password" value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    minLength={8} maxLength={64} required className="input" />
                </div>

                <button type="submit" disabled={submitting} className="btn-primary w-full justify-center">
                  {submitting ? <Loader2 size={16} className="animate-spin" /> : null}
                  Update password
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
